import { useState, useEffect, useMemo } from "react";
import {
  getTheme2SearchFilterBorderColor,
  getTheme2FormPageDarkSelectStyles,
  getTheme2FormFieldsCss,
  THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT,
  THEME2_SEARCH_FILTER_BORDER_COLOR_DARK,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

export const useLightTheme = () => {
  const [isLightTheme, setIsLightTheme] = useState(() =>
    typeof document !== "undefined"
      ? document.body.classList.contains("white-content")
      : true
  );

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isLightTheme;
};

const getThemeColors = (isTheme2, isLightTheme) => {
  if (isTheme2) {
    const border = getTheme2SearchFilterBorderColor();
    if (isLightTheme) {
      return {
        panelBg: "#ffffff",
        panelBorder: border,
        fieldBg: "#ffffff",
        fieldBorder: border,
        titleColor: "#1d253b",
        headerColor: "#6c757d",
        bodyColor: "#1d253b",
        rowBorder: "#ececec",
        enumBg: "#ffffff",
        enumBorder: border,
        enumErrorBorder: "#d9534f",
        mutedColor: "#6c757d",
        checkboxLabel: "#1d253b",
        iconMuted: "#9a9a9a",
      };
    }
    return {
      panelBg: "#272A3D",
      panelBorder: border,
      fieldBg: "rgba(255, 255, 255, 0.05)",
      fieldBorder: border,
      titleColor: "rgba(255, 255, 255, 0.95)",
      headerColor: "rgba(255, 255, 255, 0.5)",
      bodyColor: "rgba(255, 255, 255, 0.95)",
      rowBorder: THEME2_SEARCH_FILTER_BORDER_COLOR_DARK,
      enumBg: "rgba(255, 255, 255, 0.05)",
      enumBorder: border,
      enumErrorBorder: "#dc3545",
      mutedColor: "rgba(255, 255, 255, 0.5)",
      checkboxLabel: "rgba(255, 255, 255, 0.85)",
      iconMuted: "rgba(255, 255, 255, 0.45)",
    };
  }

  if (isLightTheme) {
    return {
      panelBg: "#ffffff",
      panelBorder: "#e3e3e3",
      fieldBg: "#ffffff",
      fieldBorder: "#e3e3e3",
      titleColor: "#1d253b",
      headerColor: "#999999",
      bodyColor: "#1d253b",
      rowBorder: "#ececec",
      enumBg: "#ffffff",
      enumBorder: "#e3e3e3",
      enumErrorBorder: "#d9534f",
      mutedColor: "#6c757d",
      checkboxLabel: "#1d253b",
      iconMuted: "#aaaaaa",
    };
  }

  return {
    panelBg: "#27293d",
    panelBorder: "rgba(255, 255, 255, 0.12)",
    fieldBg: "#343a52",
    fieldBorder: "rgba(255, 255, 255, 0.12)",
    titleColor: "rgba(255, 255, 255, 0.95)",
    headerColor: "rgba(255, 255, 255, 0.5)",
    bodyColor: "rgba(255, 255, 255, 0.95)",
    rowBorder: "rgba(255, 255, 255, 0.08)",
    enumBg: "#343a52",
    enumBorder: "rgba(255, 255, 255, 0.12)",
    enumErrorBorder: "#dc3545",
    mutedColor: "rgba(255, 255, 255, 0.5)",
    checkboxLabel: "rgba(255, 255, 255, 0.85)",
    iconMuted: "rgba(255, 255, 255, 0.45)",
  };
};

export const useReportScheduleTheme = (isTheme2, activeColorStyle) => {
  const isLightTheme = useLightTheme();

  const colors = useMemo(
    () => getThemeColors(isTheme2, isLightTheme),
    [isTheme2, isLightTheme]
  );

  const selectStyles = useMemo(() => {
    if (!isTheme2) return undefined;
    return getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2);
  }, [isTheme2, activeColorStyle]);

  const inputStyle = useMemo(
    () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
    [isTheme2]
  );

  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".report-schedule.report-schedule--theme2",
            fieldsSelector: ".report-schedule-fields",
          })}
          .report-schedule.report-schedule--theme2 .report-schedule-fields .react-select__input-container input {
            min-width: 4px !important;
            opacity: 1 !important;
          }`
        : "",
    [isTheme2]
  );

  const scopeClassName = [
    "report-schedule",
    isTheme2 ? "report-schedule--theme2" : "report-schedule--theme1",
    isLightTheme ? "report-schedule--light" : "report-schedule--dark",
  ].join(" ");

  return {
    isLightTheme,
    colors,
    selectStyles,
    inputStyle,
    theme2FieldsCss,
    scopeClassName,
  };
};

export const getReportSchedulePanelBoxStyle = (colors, { marginTop = 0 } = {}) => ({
  backgroundColor: colors.panelBg,
  border: `1px solid ${colors.panelBorder}`,
  padding: "16px 20px",
  marginTop,
});

export const getReportScheduleThemeCss = (activeColorStyle) => `
  .report-schedule.report-schedule--theme2.card {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .report-schedule.report-schedule--theme2 > .card-body {
    padding: 0 !important;
    background: transparent !important;
  }

  .report-schedule .report-schedule-panel-box {
    padding: 16px 20px;
  }

  .report-schedule--light .report-schedule-panel-box {
    background-color: #ffffff;
    border: 1px solid ${THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT};
  }

  .report-schedule--dark .report-schedule-panel-box {
    background-color: #272A3D;
    border: 1px solid ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK};
  }

  .report-schedule--theme1.report-schedule--light .report-schedule-panel-box {
    border-color: #e3e3e3;
  }

  .report-schedule--theme1.report-schedule--dark .report-schedule-panel-box {
    background-color: #27293d;
    border-color: rgba(255, 255, 255, 0.12);
  }

  .report-schedule--theme2.report-schedule--light .form-control {
    background-color: #ffffff;
    color: #1d253b;
    border-color: ${getTheme2SearchFilterBorderColor()};
  }

  .report-schedule--theme2.report-schedule--light .form-control:focus {
    background-color: #ffffff;
    color: #1d253b;
    border-color: ${getTheme2SearchFilterBorderColor()};
    box-shadow: none;
  }

  .report-schedule--theme2.report-schedule--dark .form-control,
  .report-schedule--theme2.report-schedule--dark .form-control:focus {
    background-color: rgba(255, 255, 255, 0.05) !important;
    color: rgba(255, 255, 255, 0.95) !important;
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK} !important;
    box-shadow: none !important;
  }

  .report-schedule--theme2.report-schedule--dark .form-control::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  .report-schedule--theme2.report-schedule--dark .form-control:-webkit-autofill,
  .report-schedule--theme2.report-schedule--dark .form-control:-webkit-autofill:hover,
  .report-schedule--theme2.report-schedule--dark .form-control:-webkit-autofill:focus,
  .report-schedule--theme2.report-schedule--dark .form-control:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important;
    box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important;
    -webkit-text-fill-color: rgba(255, 255, 255, 0.95) !important;
    caret-color: rgba(255, 255, 255, 0.95) !important;
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK} !important;
    transition: background-color 99999s ease-out 0s;
  }

  .report-schedule .report-schedule-panel-title,
  .report-schedule .report-schedule-field-label,
  .report-schedule .report-schedule-filter-field-name,
  .report-schedule .report-schedule-filter-datasource {
    color: inherit;
  }

  .report-schedule .form-check-input {
    cursor: pointer;
  }

  .report-schedule--dark .form-check-input:not(:checked) {
    background-color: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.35);
  }

  .report-schedule--light .form-check-input:not(:checked) {
    background-color: #ffffff;
    border-color: rgba(0, 0, 0, 0.25);
  }

  .report-schedule .form-check-input:checked {
    background-color: ${activeColorStyle?.backgroundColor || "#1f8ef1"};
    border-color: ${activeColorStyle?.borderColor || "#1f8ef1"};
  }

  .report-schedule .form-check-input:focus {
    box-shadow: none;
  }

  .report-schedule--dark .form-control {
    background-color: rgba(255, 255, 255, 0.05) !important;
    color: rgba(255, 255, 255, 0.95) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
  }

  .report-schedule--dark .form-control::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  .report-schedule--dark .form-control:focus {
    background-color: rgba(255, 255, 255, 0.08) !important;
    color: rgba(255, 255, 255, 0.95) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    box-shadow: none !important;
  }

  .report-schedule--dark .form-control:-webkit-autofill,
  .report-schedule--dark .form-control:-webkit-autofill:hover,
  .report-schedule--dark .form-control:-webkit-autofill:focus,
  .report-schedule--dark .form-control:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important;
    box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important;
    -webkit-text-fill-color: rgba(255, 255, 255, 0.95) !important;
    caret-color: rgba(255, 255, 255, 0.95) !important;
    transition: background-color 99999s ease-out 0s;
  }

  .report-schedule .report-schedule-enum-box {
    /* Enum checkbox box'u tablo hücresine daha yakın hizalamak için üst padding'i düşürüyoruz */
    padding: 6px 12px 6px 16px !important;
    overflow: hidden;
  }

  .report-schedule .report-schedule-enum-check-row {
    display: flex;
    align-items: center;
    padding-left: 4px;
    min-height: 24px;
    margin-bottom: 4px;
  }

  .report-schedule .report-schedule-enum-checkbox {
    position: static !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 18px;
    height: 18px;
    min-width: 18px;
    margin: 0 8px 0 0;
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 0 !important;
    -webkit-appearance: none;
    appearance: none;
    border: 1px solid #818181;
    background-color: #ffffff;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 12px 12px;
    vertical-align: middle;
  }

  .report-schedule--light .report-schedule-enum-checkbox:not(:checked) {
    border: 1px solid #818181 !important;
    background-color: #ffffff !important;
  }

  .report-schedule--dark .report-schedule-enum-checkbox:not(:checked) {
    border: 1px solid ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK} !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
  }

  .report-schedule .report-schedule-enum-checkbox:checked {
    background-color: ${activeColorStyle?.backgroundColor || "#1f8ef1"} !important;
    border-color: ${activeColorStyle?.borderColor || "#1f8ef1"} !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e") !important;
  }

  .report-schedule .report-schedule-enum-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 0.15rem rgba(29, 140, 248, 0.25);
  }

  .report-schedule .report-schedule-enum-checkbox-label {
    cursor: pointer;
    margin-bottom: 0;
    user-select: none;
  }

  .report-schedule .report-schedule-frequency-row {
    display: flex;
    align-items: center;
    min-height: 30px;
    margin-bottom: 6px;
  }

  .report-schedule .report-schedule-frequency-radio {
    position: static !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 18px;
    height: 18px;
    min-width: 18px;
    margin: 0 10px 0 0;
    flex-shrink: 0;
    cursor: pointer;
    accent-color: ${activeColorStyle?.borderColor || "#1f8ef1"};
  }

  .report-schedule--light .report-schedule-frequency-radio:not(:checked) {
    border: 1px solid #818181;
  }

  .report-schedule .report-schedule-frequency-label {
    margin-bottom: 0;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .report-schedule .report-schedule-time-input {
    max-width: 140px;
  }

  .report-schedule .report-schedule-recipients-picker {
    overflow: hidden;
  }

  .report-schedule .report-schedule-recipients-search {
    min-height: 40px;
  }

  .report-schedule .report-schedule-recipients-search-input:focus {
    box-shadow: none !important;
    outline: none;
  }

  .report-schedule .report-schedule-recipients-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 4px 0;
  }

  .report-schedule .report-schedule-recipients-item {
    min-height: 28px;
    padding: 2px 12px 2px 4px;
  }

  .report-schedule .report-schedule-recipients-letter {
    width: 18px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    margin-right: 4px;
  }

  .report-schedule .report-schedule-recipients-name {
    margin-left: 8px;
    cursor: pointer;
    user-select: none;
    font-size: 14px;
  }

  .report-schedule .report-schedule-recipients-selected {
    padding: 8px 4px;
    min-height: 280px;
  }

  .report-schedule .report-schedule-recipients-history-row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
    margin-top: 8px;
    width: 100%;
  }

  .report-schedule .report-schedule-recipients-history-col {
    flex: 1 1 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 992px) {
    .report-schedule .report-schedule-recipients-history-col {
      flex: 1 1 calc(50% - 4px);
    }
  }

  .report-schedule .report-schedule-recipients--fill,
  .report-schedule .report-schedule-history-wrap--fill {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .report-schedule .report-schedule-recipients--fill .report-schedule-recipients-picker,
  .report-schedule .report-schedule-recipients--fill .report-schedule-recipients-selected {
    min-height: 280px;
  }

  .report-schedule .report-schedule-history-panel {
    margin-top: 0;
  }

  .report-schedule .report-schedule-history-wrap--fill .report-schedule-history-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .report-schedule .report-schedule-history-wrap .report-schedule-history-calendar {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .report-schedule .report-schedule-history-hint {
    flex-shrink: 0;
  }

  .report-schedule .report-schedule-recipients-selected-name {
    font-size: 14px;
    line-height: 1.6;
    font-weight: 500;
  }

  .report-schedule .report-schedule-validation-alert,
  .report-schedule .report-schedule-theme-alert {
    border-width: 1px;
    border-style: solid;
  }

  .report-schedule--dark .report-schedule-run-history-table th,
  .report-schedule--dark .report-schedule-run-history-table td {
    color: rgba(255, 255, 255, 0.95);
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK};
  }

  .report-schedule--light .report-schedule-run-history-table th,
  .report-schedule--light .report-schedule-run-history-table td {
    color: #1d253b;
    border-color: #ececec;
  }

  .report-schedule .report-schedule-calendar-grid {
    overflow: hidden;
  }

  .report-schedule .report-schedule-calendar-weekdays,
  .report-schedule .report-schedule-calendar-days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .report-schedule .report-schedule-calendar-weekday {
    padding: 8px 4px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .report-schedule .report-schedule-calendar-day {
    min-height: 72px;
    padding: 6px;
    border: none;
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }

  .report-schedule .report-schedule-calendar-day:disabled {
    opacity: 0.55;
  }

  .report-schedule .report-schedule-calendar-day-number {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
  }

  .report-schedule .report-schedule-calendar-day-markers {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 10px;
  }

  .report-schedule .report-schedule-calendar-marker {
    width: 8px;
    height: 8px;
    display: inline-block;
  }

  .report-schedule .report-schedule-calendar-marker--scheduled {
    background: transparent;
    border: 2px solid #1d8cf8;
    width: 7px;
    height: 7px;
  }

  .report-schedule .report-schedule-calendar-run-item:last-child {
    margin-bottom: 0;
  }
`;
